import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClient } from 'urql';
import { CheckCircle2, ChevronDown, CircleOff, Download, Plus, RefreshCcw, Search, Trash2, UserX } from 'lucide-react';
import Swal from 'sweetalert2';
import PageHeader from '../../../components/Layout/PageHeader.jsx';
import { Button } from '../../../components/ui/FormComponents.jsx';
import { EmptyState, SkeletonTable } from '../../../components/ui/StateComponents.jsx';
import {
  useChoferesList,
  useChoferMutations,
  mapChoferFilters,
  mapChoferOrder,
  PAGE_SIZE_CHOFERES,
} from '../../../hooks/useChoferes.js';
import { CHOFERES_LIST } from '../../../graphql/choferes.gql.js';

const ESTADO_FILTERS = [
  { label: 'Activos', value: 'ACTIVO' },
  { label: 'Suspendidos', value: 'SUSPENDIDO' },
  { label: 'Vencidos', value: 'VENCIDO' },
  { label: 'Eliminados', value: 'ELIMINADO' },
];

const HEADERS = [
  { key: 'estado', label: 'Estado' },
  { key: 'nombreCompleto', label: 'Nombre completo' },
  { key: 'nroDocumento', label: 'Documento' },
  { key: 'licencia', label: 'Licencia' },
  { key: 'telefonoMovil', label: 'Teléfono' },
  { key: 'email', label: 'Email' },
  { key: 'sucursal', label: 'Sucursal' },
  { key: 'empresa', label: 'Empresa' },
  { key: 'fechaAlta', label: 'Fecha alta' },
  { key: 'updatedAt', label: 'Últ. actualización' },
  { key: 'acciones', label: 'Acciones', sortable: false },
];

const EXPORT_PAGE_SIZE = 200;

function buildOrder(currentOrder, field) {
  if (field === 'acciones') return currentOrder;
  if (!currentOrder || currentOrder.field !== field) {
    return { field, direction: 'asc' };
  }
  return { field, direction: currentOrder.direction === 'asc' ? 'desc' : 'asc' };
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
}

function getLicenciaChip(licenciaVencimiento) {
  if (!licenciaVencimiento) {
    return { tone: 'muted', label: 'Sin datos' };
  }
  const date = new Date(licenciaVencimiento);
  if (Number.isNaN(date.getTime())) {
    return { tone: 'muted', label: 'Sin datos' };
  }
  const now = new Date();
  const diff = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
  if (diff < 0) {
    return { tone: 'danger', label: 'Vencida' };
  }
  if (diff <= 30) {
    return { tone: 'warning', label: `Vence en ${diff} día${diff === 1 ? '' : 's'}` };
  }
  return { tone: 'success', label: `Vigente (${formatDate(licenciaVencimiento)})` };
}

function toneClassName(tone) {
  switch (tone) {
    case 'success':
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200';
    case 'warning':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200';
    case 'danger':
      return 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-200';
    default:
      return 'bg-[rgba(var(--subpanel),0.7)] text-[rgb(var(--muted))]';
  }
}

function estadoBadge(estado, deletedAt) {
  if (deletedAt) {
    return { label: 'Eliminado', className: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-200' };
  }
  if (estado === 'ACTIVO') {
    return { label: 'Activo', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200' };
  }
  if (estado === 'SUSPENDIDO') {
    return { label: 'Suspendido', className: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200' };
  }
  return { label: estado || 'Desconocido', className: 'bg-[rgba(var(--subpanel),0.7)] text-[rgb(var(--muted))]' };
}

function toCsv(rows = []) {
  const header = ['Estado', 'Nombre', 'Documento', 'Licencia', 'Teléfono', 'Email', 'Sucursal', 'Empresa', 'FechaAlta', 'Actualizado'];
  const csvRows = rows.map(row => [
    row.deletedAt ? 'ELIMINADO' : row.estado,
    row.nombreCompleto,
    `${row.tipoDocumento} ${row.nroDocumento}`,
    row.licenciaClase?.join('/') ?? '',
    row.telefonoMovil,
    row.email ?? '',
    row.sucursalNombre ?? row.sucursalId ?? '',
    row.empresaNombre ?? row.empresaId ?? '',
    formatDate(row.fechaAlta),
    formatDate(row.updatedAt),
  ]);
  return [header, ...csvRows]
    .map(columns => columns.map(value => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
}

const INITIAL_FILTER_STATE = {
  search: '',
  empresaId: '',
  sucursalId: '',
  estado: [],
  licenciaDesde: '',
  licenciaHasta: '',
  fechaAltaDesde: '',
  fechaAltaHasta: '',
  actualizadoDesde: '',
  actualizadoHasta: '',
  incluirEliminados: false,
};

export default function ListaChoferes() {
  const navigate = useNavigate();
  const client = useClient();
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState({ field: 'fechaAlta', direction: 'desc' });
  const [filterState, setFilterState] = useState(INITIAL_FILTER_STATE);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTER_STATE);
  const [selectedIds, setSelectedIds] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const choferesListQuery = useChoferesList({
    page,
    pageSize: PAGE_SIZE_CHOFERES,
    filter: appliedFilters,
    order,
  });
  const queryState = Array.isArray(choferesListQuery) ? choferesListQuery[0] : choferesListQuery;
  const reexecuteQuery = Array.isArray(choferesListQuery) ? choferesListQuery[1] : () => {};
  const { bulkAction, softDelete, restore } = useChoferMutations();

  const { data, fetching, error } = queryState || {};

  useEffect(() => {
    setSelectedIds([]);
  }, [page, appliedFilters, order]);

  const rows = Array.isArray(data?.choferes?.rows) ? data.choferes.rows : [];
  const rawPageInfo = data?.choferes?.pageInfo;
  const pageInfo =
    rawPageInfo && typeof rawPageInfo === 'object'
      ? rawPageInfo
      : { page: 1, pageSize: PAGE_SIZE_CHOFERES, hasNext: false, hasPrev: false };
  const total = Number.isFinite(Number(data?.choferes?.total)) ? Number(data.choferes.total) : 0;

  const handleFilterChange = event => {
    const { name, value, type, checked } = event.target;
    setFilterState(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const toggleEstado = value => {
    setFilterState(prev => {
      const set = new Set(prev.estado ?? []);
      if (set.has(value)) {
        set.delete(value);
      } else {
        set.add(value);
      }
      return { ...prev, estado: Array.from(set) };
    });
  };

  const applyQuickFilters = useCallback(patch => {
    setPage(1);
    setFilterState(prev => {
      const next = { ...prev, ...patch };
      setAppliedFilters(next);
      return next;
    });
  }, []);

  const handleApplyFilters = event => {
    event.preventDefault();
    setPage(1);
    setAppliedFilters(filterState);
  };

  const handleClearFilters = () => {
    setFilterState(INITIAL_FILTER_STATE);
    setAppliedFilters(INITIAL_FILTER_STATE);
    setPage(1);
  };

  const handleQuickSearchKeyDown = event => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    setPage(1);
    setAppliedFilters(filterState);
  };

  const toggleSelectAll = checked => {
    if (checked) {
      setSelectedIds(rows.map(row => row.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleSelectRow = id => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]));
  };

  const handleOrderChange = field => {
    const nextOrder = buildOrder(order, field);
    if (nextOrder !== order) {
      setOrder(nextOrder);
    }
  };

  const confirmAction = useCallback(async ({ title, text, confirmButtonText, icon = 'warning' }) => {
    const result = await Swal.fire({
      title,
      text,
      icon,
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#009688',
      cancelButtonColor: '#64748b',
      reverseButtons: true,
    });
    return result.isConfirmed;
  }, []);

  const handleBulk = async action => {
    if (selectedIds.length === 0) return;
    const titles = {
      ACTIVAR: '¿Activar choferes seleccionados?',
      SUSPENDER: '¿Suspender choferes seleccionados?',
      RESTAURAR: '¿Restaurar choferes seleccionados?',
      ELIMINAR: '¿Eliminar choferes seleccionados?',
    };
    const messages = {
      ACTIVAR: 'Los choferes quedarán activos de inmediato.',
      SUSPENDER: 'Los choferes no podrán asignarse hasta reactivarlos.',
      RESTAURAR: 'Los choferes volverán a estar disponibles.',
      ELIMINAR: 'Se realizará un borrado lógico; podés restaurarlos luego.',
    };
    const confirmed = await confirmAction({
      title: titles[action],
      text: messages[action],
      confirmButtonText: 'Confirmar',
      icon: action === 'ELIMINAR' ? 'error' : 'question',
    });
    if (!confirmed) return;
    try {
      const result = await bulkAction(selectedIds, action);
      if (result?.failed > 0) {
        await Swal.fire('Acción completada con avisos', `${result.failed} registros no pudieron actualizarse.`, 'warning');
      } else {
        await Swal.fire('Acción exitosa', 'La operación se completó correctamente.', 'success');
      }
      setSelectedIds([]);
      reexecuteQuery({ requestPolicy: 'network-only' });
    } catch (mutationError) {
      await Swal.fire('Error', mutationError.message, 'error');
    }
  };

  const handleSoftDelete = async id => {
    const confirmed = await confirmAction({
      title: '¿Eliminar chofer?',
      text: 'El chofer quedará inactivo y oculto del listado por defecto.',
      confirmButtonText: 'Eliminar',
      icon: 'error',
    });
    if (!confirmed) return;
    try {
      await softDelete(id);
      await Swal.fire('Chofer eliminado', 'Podés restaurarlo desde acciones masivas.', 'success');
      reexecuteQuery({ requestPolicy: 'network-only' });
    } catch (mutationError) {
      await Swal.fire('Error', mutationError.message, 'error');
    }
  };

  const handleRestore = async id => {
    try {
      await restore(id);
      await Swal.fire('Chofer restaurado', 'El chofer vuelve a estar disponible.', 'success');
      reexecuteQuery({ requestPolicy: 'network-only' });
    } catch (mutationError) {
      await Swal.fire('Error', mutationError.message, 'error');
    }
  };

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const filterVariables = mapChoferFilters(appliedFilters);
      const orderVariables = mapChoferOrder(order);
      let currentPage = 1;
      const aggregated = [];
      let keepFetching = true;

      while (keepFetching) {
        const result = await client
          .query(
            CHOFERES_LIST,
            {
              page: currentPage,
              pageSize: EXPORT_PAGE_SIZE,
              filter: filterVariables,
              order: orderVariables,
            },
            { requestPolicy: 'network-only' },
          )
          .toPromise();

        if (result.error) {
          throw result.error;
        }

        const chunk = result.data?.choferes?.rows ?? [];
        aggregated.push(...chunk);
        const info = result.data?.choferes?.pageInfo;

        if (!info || !info.hasNext || chunk.length === 0) {
          keepFetching = false;
        } else {
          currentPage += 1;
        }
      }

      if (aggregated.length === 0) {
        await Swal.fire('Sin resultados', 'No hay choferes para exportar con los filtros actuales.', 'info');
        return;
      }

      const csv = toCsv(aggregated);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      link.setAttribute('download', `choferes-${timestamp}.csv`);
      link.click();
      URL.revokeObjectURL(url);

      await Swal.fire('Export listo', 'Descargamos el CSV aplicando tus filtros actuales.', 'success');
    } catch (exportError) {
      console.error('Exportación de choferes', exportError);
      await Swal.fire('Error', exportError.message || 'No se pudo exportar el listado.', 'error');
    } finally {
      setExporting(false);
    }
  }, [appliedFilters, order, client]);

  const filterSummary = useMemo(() => {
    const mapped = mapChoferFilters(appliedFilters) || {};
    return Object.keys(mapped).length;
  }, [appliedFilters]);

  const toolbarEstadoValue = useMemo(() => {
    if (filterState.incluirEliminados && (filterState.estado?.length ?? 0) === 0) {
      return 'ELIMINADO';
    }
    if ((filterState.estado?.length ?? 0) === 1 && !filterState.incluirEliminados) {
      return filterState.estado[0];
    }
    if ((filterState.estado?.length ?? 0) > 1 || filterState.incluirEliminados) {
      return 'MULTIPLE';
    }
    return '';
  }, [filterState.estado, filterState.incluirEliminados]);

  const handleToolbarEstadoChange = event => {
    const { value } = event.target;
    if (!value) {
      applyQuickFilters({ estado: [], incluirEliminados: false });
      return;
    }
    if (value === 'ELIMINADO') {
      applyQuickFilters({ estado: [], incluirEliminados: true });
      return;
    }
    if (value === 'MULTIPLE') {
      setShowAdvancedFilters(true);
      return;
    }
    applyQuickFilters({ estado: [value], incluirEliminados: false });
  };

  return (
    <div className="mx-auto max-w-[1440px] space-y-4 px-4 pb-24">
      <PageHeader
        title="Choferes"
        subtitle="Administrá el padrón de choferes con filtros avanzados y acciones masivas."
        breadcrumbs={[
          { label: 'Administración', href: '/administracion' },
          { label: 'Choferes' },
        ]}
      />

      <section className="space-y-3">
        <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.92)] px-4 py-3 shadow-[0_14px_32px_rgba(var(--shadow),0.07)]">
          <form className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between" onSubmit={handleApplyFilters}>
            <div className="flex flex-1 flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center">
              <div className="relative min-w-[260px] flex-1 xl:max-w-[420px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--muted))]" />
                <input
                  id="search"
                  name="search"
                  value={filterState.search}
                  onChange={event => setFilterState(prev => ({ ...prev, search: event.target.value }))}
                  onBlur={() => setAppliedFilters(prev => ({ ...prev, search: filterState.search }))}
                  onKeyDown={handleQuickSearchKeyDown}
                  className="h-10 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] py-2 pl-9 pr-3 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  placeholder="Buscar por nombre, documento o teléfono"
                />
              </div>

              <select
                aria-label="Filtrar por estado"
                value={toolbarEstadoValue}
                onChange={handleToolbarEstadoChange}
                className="h-10 min-w-[160px] rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] px-3 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                <option value="">Todos los estados</option>
                {ESTADO_FILTERS.filter(option => option.value !== 'ELIMINADO').map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
                <option value="ELIMINADO">Eliminados</option>
                {toolbarEstadoValue === 'MULTIPLE' ? <option value="MULTIPLE">Combinado</option> : null}
              </select>

              <input
                id="sucursalId"
                name="sucursalId"
                value={filterState.sucursalId}
                onChange={event => setFilterState(prev => ({ ...prev, sucursalId: event.target.value }))}
                onBlur={() => setAppliedFilters(prev => ({ ...prev, sucursalId: filterState.sucursalId }))}
                className="h-10 min-w-[180px] rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] px-3 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                placeholder="Sucursal"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAdvancedFilters(prev => !prev)}
                className="border border-[rgb(var(--border))]"
              >
                <ChevronDown className={`mr-2 h-4 w-4 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
                Filtros avanzados
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={handleExport} disabled={exporting || total === 0} aria-busy={exporting}>
                <Download className="mr-2 h-4 w-4" />
                {exporting ? 'Exportando…' : 'Exportar CSV'}
              </Button>
              <Button type="button" size="sm" onClick={() => navigate('/administracion/choferes/nuevo')}>
                <Plus className="mr-2 h-4 w-4" />
                Nuevo chofer
              </Button>
            </div>
          </form>

          {showAdvancedFilters ? (
            <form className="mt-3 border-t border-[rgba(var(--border),0.7)] pt-3" onSubmit={handleApplyFilters}>
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-12 sm:col-span-6 lg:col-span-4">
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-[rgb(var(--muted))]" htmlFor="empresaId">
                    Empresa
                  </label>
                  <input
                    id="empresaId"
                    name="empresaId"
                    value={filterState.empresaId}
                    onChange={handleFilterChange}
                    className="h-10 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] px-3 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                    placeholder="ID o nombre de empresa"
                  />
                </div>
                <div className="col-span-12 sm:col-span-6 lg:col-span-4">
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-[rgb(var(--muted))]" htmlFor="advancedSucursalId">
                    Sucursal
                  </label>
                  <input
                    id="advancedSucursalId"
                    name="sucursalId"
                    value={filterState.sucursalId}
                    onChange={handleFilterChange}
                    className="h-10 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] px-3 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                    placeholder="ID o nombre de sucursal"
                  />
                </div>
                <div className="col-span-12 lg:col-span-4">
                  <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-[rgb(var(--muted))]">Estado</span>
                  <div className="flex flex-wrap gap-2">
                    {ESTADO_FILTERS.map(option => {
                      const active = filterState.estado?.includes(option.value);
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => toggleEstado(option.value)}
                          className={`rounded-full border px-3 py-1 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] focus-visible:ring-offset-0 ${
                            active
                              ? 'border-[rgb(var(--brand))] bg-[rgba(var(--brand),0.12)] text-[rgb(var(--brand-700))]'
                              : 'border-[rgb(var(--border))] bg-[rgba(var(--panel),0.5)] text-[rgb(var(--muted))] hover:border-[rgb(var(--brand))]'
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-6">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-[rgb(var(--muted))]" htmlFor="licenciaDesde">
                        Licencia desde
                      </label>
                      <input
                        id="licenciaDesde"
                        type="date"
                        name="licenciaDesde"
                        value={filterState.licenciaDesde}
                        onChange={handleFilterChange}
                        className="h-10 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] px-3 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-[rgb(var(--muted))]" htmlFor="licenciaHasta">
                        Licencia hasta
                      </label>
                      <input
                        id="licenciaHasta"
                        type="date"
                        name="licenciaHasta"
                        value={filterState.licenciaHasta}
                        onChange={handleFilterChange}
                        className="h-10 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] px-3 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-6">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-[rgb(var(--muted))]" htmlFor="fechaAltaDesde">
                        Alta desde
                      </label>
                      <input
                        id="fechaAltaDesde"
                        type="date"
                        name="fechaAltaDesde"
                        value={filterState.fechaAltaDesde}
                        onChange={handleFilterChange}
                        className="h-10 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] px-3 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-[rgb(var(--muted))]" htmlFor="fechaAltaHasta">
                        Alta hasta
                      </label>
                      <input
                        id="fechaAltaHasta"
                        type="date"
                        name="fechaAltaHasta"
                        value={filterState.fechaAltaHasta}
                        onChange={handleFilterChange}
                        className="h-10 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] px-3 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-6">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-[rgb(var(--muted))]" htmlFor="actualizadoDesde">
                        Actualizado desde
                      </label>
                      <input
                        id="actualizadoDesde"
                        type="date"
                        name="actualizadoDesde"
                        value={filterState.actualizadoDesde}
                        onChange={handleFilterChange}
                        className="h-10 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] px-3 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-[rgb(var(--muted))]" htmlFor="actualizadoHasta">
                        Actualizado hasta
                      </label>
                      <input
                        id="actualizadoHasta"
                        type="date"
                        name="actualizadoHasta"
                        value={filterState.actualizadoHasta}
                        onChange={handleFilterChange}
                        className="h-10 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] px-3 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-12">
                  <label className="inline-flex items-center gap-2 text-sm text-[rgb(var(--fg))]">
                    <input
                      type="checkbox"
                      name="incluirEliminados"
                      checked={filterState.incluirEliminados}
                      onChange={handleFilterChange}
                      className="h-4 w-4 rounded border-[rgb(var(--border))] text-[rgb(var(--brand))] focus:ring-[rgb(var(--ring))]"
                    />
                    Mostrar choferes eliminados
                  </label>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-[rgba(var(--border),0.7)] pt-3">
                <span className="text-xs text-[rgb(var(--muted))]">
                  {filterSummary > 0 ? `${filterSummary} filtros aplicados` : 'Sin filtros avanzados'}
                </span>
                <div className="flex gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={handleClearFilters}>
                    Limpiar
                  </Button>
                  <Button type="submit" size="sm">
                    Aplicar filtros
                  </Button>
                </div>
              </div>
            </form>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => handleBulk('ACTIVAR')} disabled={selectedIds.length === 0}>
              <CheckCircle2 className="mr-2 h-4 w-4" /> Activar
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={() => handleBulk('SUSPENDER')} disabled={selectedIds.length === 0}>
              <CircleOff className="mr-2 h-4 w-4" /> Suspender
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={() => handleBulk('RESTAURAR')} disabled={selectedIds.length === 0}>
              <RefreshCcw className="mr-2 h-4 w-4" /> Restaurar
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={() => handleBulk('ELIMINAR')} disabled={selectedIds.length === 0}>
              <Trash2 className="mr-2 h-4 w-4" /> Eliminar
            </Button>
          </div>
          <div className="flex items-center gap-2 text-xs text-[rgb(var(--muted))]">
            <span>{total} registros</span>
            <span className="hidden sm:inline">•</span>
            <span>{selectedIds.length} seleccionados</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => reexecuteQuery({ requestPolicy: 'network-only' })}>
              <RefreshCcw className="mr-2 h-4 w-4" />
              Actualizar
            </Button>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.85)] shadow-[0_18px_38px_rgba(var(--shadow),0.09)]">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[rgba(var(--border),0.7)]">
              <thead className="bg-[rgba(var(--subpanel),0.7)] text-xs uppercase tracking-wide text-[rgb(var(--muted))]">
                <tr>
                  <th className="px-3 py-3">
                    <input
                      type="checkbox"
                      aria-label="Seleccionar todos"
                      checked={rows.length > 0 && selectedIds.length === rows.length}
                      onChange={event => toggleSelectAll(event.target.checked)}
                      className="h-4 w-4 rounded border-[rgb(var(--border))] text-[rgb(var(--brand))] focus:ring-[rgb(var(--ring))]"
                    />
                  </th>
                  {HEADERS.map(header => (
                    <th key={header.key} className="px-3 py-3">
                      {header.sortable === false ? (
                        <span className="text-left text-xs font-semibold text-[rgb(var(--muted))]">{header.label}</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleOrderChange(header.key)}
                          className="flex items-center gap-1 text-left text-xs font-semibold uppercase tracking-wide text-[rgb(var(--muted))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] focus-visible:ring-offset-0"
                        >
                          {header.label}
                          {order.field === header.key ? <span>{order.direction === 'asc' ? '↑' : '↓'}</span> : null}
                        </button>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(var(--border),0.6)] text-sm text-[rgb(var(--fg))]">
                {fetching && rows.length === 0 ? (
                  <tr>
                    <td colSpan={HEADERS.length + 1} className="px-4 py-6">
                      <SkeletonTable rows={4} columns={HEADERS.length + 1} />
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={HEADERS.length + 1} className="px-4 py-8">
                      <EmptyState
                        title="No encontramos choferes con esos filtros"
                        subtitle="Ajustá los filtros o cargá un nuevo chofer."
                        primary={
                          <Button type="button" onClick={() => navigate('/administracion/choferes/nuevo')}>
                            Crear chofer
                          </Button>
                        }
                      />
                    </td>
                  </tr>
                ) : (
                  rows.map(row => {
                    const licencia = getLicenciaChip(row.licenciaVencimiento);
                    const estadoInfo = estadoBadge(row.estado, row.deletedAt);
                    const isSelected = selectedIds.includes(row.id);
                    return (
                      <tr key={row.id} className="bg-[rgba(var(--panel),0.65)] transition hover:bg-[rgba(var(--subpanel),0.6)]">
                        <td className="px-3 py-2.5">
                          <input
                            type="checkbox"
                            aria-label={`Seleccionar ${row.nombreCompleto}`}
                            checked={isSelected}
                            onChange={() => toggleSelectRow(row.id)}
                            className="h-4 w-4 rounded border-[rgb(var(--border))] text-[rgb(var(--brand))] focus:ring-[rgb(var(--ring))]"
                          />
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${estadoInfo.className}`}>
                            {estadoInfo.label}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 font-semibold">{row.nombreCompleto}</td>
                        <td className="px-3 py-2.5">{`${row.tipoDocumento} ${row.nroDocumento}`}</td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${toneClassName(licencia.tone)}`}>
                            {licencia.label}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">{row.telefonoMovil}</td>
                        <td className="px-3 py-2.5">{row.email || '—'}</td>
                        <td className="px-3 py-2.5">{row.sucursalNombre || row.sucursalId || '—'}</td>
                        <td className="px-3 py-2.5">{row.empresaNombre || row.empresaId || '—'}</td>
                        <td className="px-3 py-2.5">{formatDate(row.fechaAlta)}</td>
                        <td className="px-3 py-2.5">{formatDate(row.updatedAt)}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            <Button type="button" variant="ghost" size="sm" onClick={() => navigate(`/administracion/choferes/${row.id}`)}>
                              Ver
                            </Button>
                            <Button type="button" variant="ghost" size="sm" onClick={() => navigate(`/administracion/choferes/${row.id}/editar`)}>
                              Editar
                            </Button>
                            {row.deletedAt ? (
                              <Button type="button" variant="ghost" size="sm" onClick={() => handleRestore(row.id)}>
                                Restaurar
                              </Button>
                            ) : (
                              <Button type="button" variant="ghost" size="sm" onClick={() => handleSoftDelete(row.id)}>
                                <UserX className="mr-1 h-4 w-4" />
                                Baja
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {error ? (
            <div className="border-t border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.45)] px-6 py-4 text-sm text-red-500">
              <p className="font-medium">No se pudo cargar la lista de choferes.</p>
              <p className="mt-1 text-xs opacity-80">{error.message}</p>
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-[rgb(var(--muted))]">
          <span>
            Mostrando {(page - 1) * PAGE_SIZE_CHOFERES + (rows.length > 0 ? 1 : 0)}-{(page - 1) * PAGE_SIZE_CHOFERES + rows.length} de {total}
          </span>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={() => setPage(prev => Math.max(1, prev - 1))} disabled={!pageInfo.hasPrev}>
              Anterior
            </Button>
            <span className="rounded-full bg-[rgba(var(--subpanel),0.6)] px-3 py-1 text-xs font-medium text-[rgb(var(--muted))]">
              Página {pageInfo.page}
            </span>
            <Button type="button" variant="ghost" onClick={() => setPage(prev => prev + 1)} disabled={!pageInfo.hasNext}>
              Siguiente
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
